import { User } from "oidc-client-ts";
import OneTutorFavicon from "/favicon.ico?url";
import TumTutorFavicon from "/favicon_tumtutor.ico?url";
import LogoOneTutor from "/onetutor.svg?url";
import LogoWhiteOneTutor from "/onetutor_white.svg?url";
import LogoTUMTutor from "/TUM_Logo.svg?url";
import LogoWhiteTUMTutor from "/TUM_Logo_white.svg?url";

const origin = window.location.origin;

const AUTHORITY =
  "https://cognito-idp.eu-central-1.amazonaws.com/eu-central-1_1W0M53reW";

const ENV_CONFIG = [
  {
    pattern: /^http:\/\/localhost/,
    backendUrl: `${origin}/api`,
    clientId: "5q93209mcsl65snj42ppgk1ahr",
  },
  {
    pattern: /^https:\/\/staging\.onetutor\.(?:ai|de)$/,
    backendUrl: "https://staging.onetutor.ai/api",
    clientId: "k46qjfv6s4p5n408vvs3oiqoi",
  },
  {
    pattern: /^https:\/\/app\.onetutor\.(?:ai|de)$/,
    backendUrl: "https://app.onetutor.ai/api",
    clientId: "1e6ks365jq8ad39p8es4dirqk9",
  },
];

const instanceConfigMapping = [
  {
    name: "TUMTutor",
    pattern: /^tumtutor\.(?:ai|de)$/,
    lightMode: LogoTUMTutor,
    darkMode: LogoWhiteTUMTutor,
    alt: "TUMTutor Logo",
    favicon: TumTutorFavicon,
  },
];

const fallbackInstanceConfig = {
  name: "OneTutor",
  lightMode: LogoOneTutor,
  darkMode: LogoWhiteOneTutor,
  alt: "OneTutor Logo",
  favicon: OneTutorFavicon,
};

// used for all *.onetutor.ai subdomains
const defaultConfig = {
  backendUrl: "https://app.onetutor.ai/api",
  clientId: "24qrs5drplq2qrpi2u3stmn0o",
};

const { backendUrl, clientId } =
  ENV_CONFIG.find(({ pattern }) => pattern.test(origin)) || defaultConfig;

const instanceConfig =
  instanceConfigMapping.find(({ pattern }) =>
    pattern.test(window.location.hostname),
  ) || fallbackInstanceConfig;

/**
 * Calls the backend with the id_token to properly create or update the user.
 * This ensures that the user exists in the backend before relying solely on the access_token for further API requests.
 *
 * The function retrieves the user information from the session storage, constructs a user object, and sends a POST request to the backend authentication endpoint with the id_token.
 * @throws {Error} If the user is not found in the session storage or the login fails.
 */
async function backendLogin(): Promise<void> {
  const oidcStorage = sessionStorage.getItem(
    `oidc.user:${AUTHORITY}:${clientId}`,
  );
  if (!oidcStorage) {
    throw new Error("No user found in storage");
  }
  const user = User.fromStorageString(oidcStorage);
  const response = await fetch(`${backendUrl}/auth/login`, {
    method: "POST",
    headers: { Authorization: `Bearer ${user.id_token}` },
  });
  if (!response.ok) {
    throw new Error(
      `Failed to login to backend. Status: ${response.status} ${response.statusText}`,
    );
  }
}

export const environment = {
  name: import.meta.env.MODE,
  backendUrl,
  sentrySampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
  oidc: {
    authority: AUTHORITY,
    client_id: clientId,
    redirect_uri: origin,
    scope: "openid profile",
    onSigninCallback: async () => {
      await backendLogin();

      const storedRedirect = sessionStorage.getItem("redirectAfterLogin");
      sessionStorage.removeItem("redirectAfterLogin");
      if (storedRedirect) {
        window.location.href = storedRedirect;
      }
    },
  },
  instanceConfig,
  isOneTutorDomain: origin === "https://app.onetutor.ai",
};
